import { inject, Injectable } from '@angular/core';
import { Dictionary } from '@ngrx/entity';
import { produce } from 'immer';

import { CanvasElement } from '@blaze-runtime/engine-domain';
import { PageInfo } from '@blaze-shared/api-contracts-domain';
import { RuntimeMode } from '@blaze-shared/app-state-data-access';
import {
  ElementPropsUpdate,
  ElementRedirect,
  PropsUpdate,
  RuntimePage,
} from '@blaze-shared/elements-domain';
import { SchemaService } from '@blaze-shared/schema-data-access';
import {
  Connection,
  findOrAppendNavigationNodeMutably,
  hasResultMessages,
  Workflow,
} from '@blaze-shared/workflow-domain';

import {
  isEditableForm,
  isVariantOfButton,
} from '../../canvas-elements/element-model/element-type-guards';
import { ButtonPropsModel } from '../../elements/button/button.element';
import {
  DEFAULT_SUCCESS_MESSAGE,
  FormElement,
} from '../../elements/form-container/form-container.model';
import { TablePropsModel } from '../../elements/table/table.props-model';
import { hasCrudActions } from '../../utils/elements/validation.utils';
import { getSubmitWorkflow } from '../../utils/runtime/connection';

import { elementProps } from './layout-normalizer.service';

type WorkflowPropsUpdate = { elementId: string; workflow: Workflow };

@Injectable({ providedIn: 'root' })
export class LayoutPostLoadNormalizerService {
  private readonly schemaService = inject(SchemaService);

  validateElements(
    layoutId: string,
    state: any,
  ): { elementPropsUpdate?: ElementPropsUpdate[]; workflowsUpdates?: WorkflowPropsUpdate[] } {
    if (state?.router?.state?.root?.firstChild?.data?.runtimeMode !== RuntimeMode.Editor) {
      return {};
    }

    const elementPropsUpdate: ElementPropsUpdate[] = [];
    const workflowsUpdates: WorkflowPropsUpdate[] = [];
    const elementsNode = state?.engine?.elements;
    const elements: CanvasElement[] = elementsNode.ids
      .map((id: string) => elementsNode.entities[id])
      .filter((e: CanvasElement) => e.ns === layoutId);

    const workflows = state?.engine?.runtime?.workflows || [];
    const connections = state?.engine?.runtime?.connections || [];
    const runtimePages = state?.engine?.runtime?.runtimePages || [];
    const pages = state?.engine?.runtime?.pages || [];

    for (const element of elements) {
      const elementUpdates: ElementPropsUpdate = { elementId: element.id, updates: [] };

      // NOTE when adding support for itemAdd | itemEdit | itemDelete | itemView element actions
      // add component type to this array
      if (hasCrudActions(element.type)) {
        this.validateElementActions(elementUpdates, elementProps(element) as TablePropsModel, {
          workflows,
          pages,
        });
      }

      if (isEditableForm(element)) {
        this.validateFormPostAction(workflowsUpdates, elementUpdates, element as FormElement, {
          workflows,
          entities: elementsNode.entities,
          connections,
          runtimePages,
        });
      }

      if (isVariantOfButton(element)) {
        this.validateButtonAction(elementUpdates, elementProps(element) as ButtonPropsModel, {
          workflows,
          pages,
        });
      }

      if (elementUpdates.updates.length) {
        elementPropsUpdate.push(elementUpdates);
      }
    }

    return { elementPropsUpdate, workflowsUpdates };
  }

  private validateElementActions(
    elementUpdate: ElementPropsUpdate,
    props: TablePropsModel,
    data: {
      workflows: Workflow[];
      pages: PageInfo[];
    },
  ): void {
    const { workflows, pages } = data;
    const { itemAdd, itemEdit, itemDelete, itemView, itemViewDialog } = props?.content || {};
    const { updates } = elementUpdate;

    this.validateElementAction(updates, itemAdd, 'itemAdd', pages);
    this.validateElementAction(updates, itemView, 'itemView', pages);
    this.validateElementAction(updates, itemEdit, 'itemEdit', pages);
    this.validateElementAction(updates, itemDelete, 'itemDelete', workflows);
    this.validateElementAction(updates, itemViewDialog, 'itemViewDialog', pages);
  }

  private validateElementAction(
    updates: PropsUpdate[],
    action: ElementRedirect | undefined,
    actionName: string,
    collection: { id?: string }[],
  ): void {
    if (!action) {
      return;
    }

    const existingItem = findItem(collection, action.targetId);
    if (
      // when the flag is enabled or has a targetId, but the target doesn't exist
      // or the flag is enabled and doesn't have a targetId
      // -> disable the flag
      ((action.enabled || action.targetId) && !existingItem) ||
      (action?.enabled && !action.targetId)
    ) {
      updates.push(
        { path: `content.${actionName}.enabled`, value: false },
        { path: `content.${actionName}.targetId`, value: '' },
      );
    }
    // check if the action targetType matches the pageType of its target
    if (
      existingItem &&
      'pageType' in existingItem &&
      action.targetType !== (existingItem as PageInfo).pageType
    ) {
      updates.push({
        path: `content.${actionName}.targetType`,
        value: (existingItem as PageInfo).pageType,
      });
    }
  }

  private validateFormPostAction(
    workflowUpdate: WorkflowPropsUpdate[],
    propsUpdates: ElementPropsUpdate,
    element: FormElement,
    data: {
      workflows: Workflow[];
      connections: Connection[];
      entities: Dictionary<CanvasElement>;
      runtimePages: Dictionary<RuntimePage>;
    },
  ): void {
    const wf = getSubmitWorkflow(element.id, data.connections, data.workflows, data.entities);
    if (!wf) {
      return;
    }

    let wasWfModified = false;
    const workflow = produce(wf, (draft) => {
      const messageNode = draft.nodes.find((node) => hasResultMessages(node));
      const { postAction } = element.props?.content ?? {};
      const postActionMessage = postAction?.message?.trim();
      if (postActionMessage && hasResultMessages(messageNode)) {
        const defaultMsg = DEFAULT_SUCCESS_MESSAGE;
        const message = messageNode.successMessage;

        if (!message || (message && message !== postActionMessage && message === defaultMsg)) {
          messageNode.successMessage = postActionMessage;
          wasWfModified = true;
          propsUpdates.updates.push({ path: 'content.postAction.message', value: undefined });
        }
      }

      if (postAction?.actionType === 'Page' && postAction.pageId) {
        const navigationNode = findOrAppendNavigationNodeMutably(draft, {
          target: postAction.pageId,
          runtimePages: data.runtimePages,
          workflowSchema: this.schemaService.getWorkflowSchema(wf.id),
        });
        if (navigationNode.target !== postAction.pageId) {
          navigationNode.target = postAction.pageId;
          wasWfModified = true;
          propsUpdates.updates.push({ path: 'content.postAction.pageId', value: undefined });
        }
      }
    });

    if (wasWfModified) {
      workflowUpdate.push({ elementId: element.id, workflow });
    }
  }

  private validateButtonAction(
    elementUpdate: ElementPropsUpdate,
    props: ButtonPropsModel,
    data: {
      workflows: Workflow[];
      pages: PageInfo[];
    },
  ): void {
    const { workflowId } = props.content?.action || {};
    const workflow = workflowId ? data.workflows.find((wf) => wf.id === workflowId) : undefined;

    if (workflowId && !workflow) {
      elementUpdate.updates.push({ path: 'content.action', value: undefined });
    }
  }
}

function findItem(collection: { id?: string }[], targetId?: string): { id?: string } | undefined {
  return targetId ? collection.find((p) => p.id === targetId) : undefined;
}
